import { Fragment, ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cls from 'classnames';

import { isNumeric } from '@cutwise/utils';

import { useMetricScalePath } from '@/routes/DiamondPage/BasicInfo/PerformanceMetricScale/useMetricScalePath.ts';
import { useStore } from '@/stores/StoreProvider.tsx';
import { svgStringToHTML } from '@/utils/svgStringToHTML';

import styles from './PerformanceMetricScale.module.scss';

interface MetricScaleRow {
  title: ReactNode;
  value?: number;
  unit?: string;
}

interface PerformanceMetricScaleProps {
  title?: ReactNode;
  value?: number;
  unit?: string;
  rows?: MetricScaleRow[];
  metricKey: string;
  metric: Record<string, number | undefined>;
}

function PerformanceMetricScale(props: PerformanceMetricScaleProps) {
  const { title, metric, metricKey, value, unit, rows } = props;
  const { isStarMetric } = useStore('app');

  const headerRows: MetricScaleRow[] = rows ?? [{ title, value, unit }];

  const pathName = useMetricScalePath(metricKey, isStarMetric);
  const [svgContent, setSvgContent] = useState<Nullable<string>>(null);

  async function getMetric() {
    const params = new URLSearchParams();

    params.append('rs', '1');

    Object.keys(metric).forEach((key) => {
      if (typeof metric[key] !== 'undefined') {
        params.append(key, `${metric[key]}`);
      }
    });

    try {
      const svgElement = await svgStringToHTML(`${import.meta.env.MR_SCALE_ORIGIN}${pathName}?${params.toString()}`);
      const svgHTML = svgElement?.outerHTML?.replaceAll('diagonalHatch', `diagonalHatch${metricKey.replaceAll(/d/g, '')}`);

      setSvgContent(svgHTML ?? null);
    } catch (e) {
      //
    }
  }

  useEffect(() => {
    getMetric();
  }, []);

  if (!svgContent) {
    return null;
  }

  return (
    <Fragment>
      {headerRows.map((row, index) => (
        <div className={styles.root} key={index}>
          <div>{row.title}</div>

          {isNumeric(row.value) && (
            <div className={styles.value}>
              <span>{row.value!.toFixed(2)}</span>

              <span>{row.unit ? ` ${row.unit}` : ''}</span>
            </div>
          )}
        </div>
      ))}

      <div className={cls(styles.metric, { [styles.metricFullWidth]: !isStarMetric })} dangerouslySetInnerHTML={{ __html: svgContent }} />
    </Fragment>
  );
}

export default observer(PerformanceMetricScale);
